import React from 'react';
import { ShieldCheck, Clock, Building2, UserCircle2, ArrowRight } from 'lucide-react';

interface PendingApprovalProps {
  userName: string;
  userEmail: string;
  onLogout: () => void;
}

const PendingApproval: React.FC<PendingApprovalProps> = ({ userName, userEmail, onLogout }) => {
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-6 relative overflow-hidden font-sans">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 rounded-full blur-3xl mix-blend-multiply opacity-50 animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-3xl mix-blend-multiply opacity-50 animate-pulse animation-delay-2000"></div>
      </div>

      <div className="w-full max-w-xl bg-white rounded-[2rem] shadow-2xl overflow-hidden relative z-10 animate-scaleIn border border-slate-100">
        
        {/* Header Section */}
        <div className="bg-slate-900 p-10 text-center relative overflow-hidden">
           {/* Abstract shapes inside header */}
           <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full"><polygon fill="white" points="0,100 100,0 100,100"/></svg>
           </div>
           
           <div className="relative z-10 flex flex-col items-center">
             <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-3xl flex items-center justify-center mb-6 shadow-2xl border border-white/20">
               <ShieldCheck className="text-emerald-400" size={40} />
             </div>
             <h1 className="text-3xl font-black text-white tracking-tight mb-2">Bijna klaar!</h1>
             <p className="text-slate-400 font-medium text-sm">Je account is geverifieerd en beveiligd.</p>
           </div>
        </div>

        {/* Content Section */}
        <div className="p-10 space-y-8">
          <div className="text-center space-y-3">
             <h2 className="text-xl font-bold text-slate-800">Wachten op activering</h2>
             <p className="text-slate-500 text-sm leading-relaxed max-w-sm mx-auto">
               Welkom bij Werftools, <strong className="text-slate-700">{userName.split(' ')[0]}</strong>. Om de veiligheid en privacy van onze bedrijfsomgevingen te garanderen, moet een beheerder jouw account eerst koppelen aan de juiste organisatie.
             </p>
          </div>

          {/* Status Steps */}
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-6">
             <div className="flex items-start gap-4">
                <div className="mt-1 w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-200">
                  <UserCircle2 size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Account Gemaakt</h4>
                  <p className="text-xs text-slate-500 mt-1">{userEmail} is succesvol geregistreerd.</p>
                </div>
             </div>
             
             <div className="relative ml-4 border-l-2 border-dashed border-slate-200 h-6 -my-4"></div>

             <div className="flex items-start gap-4">
                <div className="mt-1 w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-200 animate-pulse">
                  <Clock size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-indigo-900">Beheerder Koppeling</h4>
                  <p className="text-xs text-indigo-700/70 mt-1">Dit duurt meestal niet langer dan een paar uur tijdens kantoortijden.</p>
                </div>
             </div>

             <div className="relative ml-4 border-l-2 border-dashed border-slate-200 h-6 -my-4"></div>

             <div className="flex items-start gap-4 opacity-50 grayscale">
                <div className="mt-1 w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center shrink-0 border border-slate-300">
                  <Building2 size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900">Toegang tot Werftools</h4>
                  <p className="text-xs text-slate-500 mt-1">Je ontvangt bericht via de beheerder zodra je erin kunt.</p>
                </div>
             </div>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-center">
             <button 
               onClick={onLogout}
               className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors py-2 px-4 rounded-xl hover:bg-slate-50"
             >
               Uitloggen <ArrowRight size={14} />
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;
